package com.milta.feature.optimizationquota.data.remote.dto

import com.milta.feature.admin.data.remote.dto.AdminAccountDto
import com.milta.feature.admin.data.remote.dto.toDomain
import com.milta.feature.optimizationquota.domain.entity.AdminAccountOptimizationQuota
import com.milta.feature.optimizationquota.domain.entity.AdminQuotaAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationAction
import com.milta.feature.optimizationquota.domain.entity.OptimizationHistoryEntry
import com.milta.feature.optimizationquota.domain.entity.OptimizationLimits
import com.milta.feature.optimizationquota.domain.entity.OptimizationOutcome
import com.milta.feature.optimizationquota.domain.entity.OptimizationQuota
import com.milta.feature.optimizationquota.domain.entity.OptimizationSubscriptionKind
import kotlinx.serialization.Serializable

// MIL-97 — field names match the backend's camelCase JSON (System.Text.Json default).

@Serializable
data class OptimizationQuotaDto(
    val remaining: Int,
    val limit: Int,
    val resetAt: String,
    val accountKind: OptimizationAccountKind,
    val subscriptionKind: OptimizationSubscriptionKind,
    val isManuallyAdjusted: Boolean = false,
    /** Global limit of the profile (≠ [limit] after an admin replacement); [limit] when absent. */
    val globalLimit: Int? = null,
)

@Serializable
data class OptimizationHistoryEntryDto(
    val id: String,
    val occurredAt: String,
    // Strings, converted leniently in toDomain(): an unknown value never fails the whole page.
    val action: String,
    val routeId: String? = null,
    val routeBatchId: String? = null,
    val routeDate: String? = null,
    val outcome: String,
)

/** Body of both GET and PUT `admin/usage-limits/optimization`. */
@Serializable
data class OptimizationLimitsDto(
    val enterpriseTrial: Int,
    val enterprisePaid: Int,
    val driverTrial: Int,
    val driverPaid: Int,
)

@Serializable
data class AdminAccountOptimizationQuotaDto(
    val account: AdminAccountDto,
    val accountKind: AdminQuotaAccountKind,
    val enterpriseName: String? = null,
    val quota: OptimizationQuotaDto,
)

@Serializable
data class ReplaceRemainingOptimizationsRequest(val remaining: Int)

fun OptimizationQuotaDto.toDomain() = OptimizationQuota(
    remaining = remaining,
    limit = limit,
    resetAt = resetAt,
    accountKind = accountKind,
    subscriptionKind = subscriptionKind,
    isManuallyAdjusted = isManuallyAdjusted,
    globalLimit = globalLimit ?: limit,
)

fun OptimizationHistoryEntryDto.toDomain() = OptimizationHistoryEntry(
    id = id,
    occurredAt = occurredAt,
    action = OptimizationAction.fromApi(action),
    routeId = routeId,
    routeBatchId = routeBatchId,
    routeDate = routeDate,
    outcome = OptimizationOutcome.fromApi(outcome),
)

fun OptimizationLimitsDto.toDomain() = OptimizationLimits(enterpriseTrial, enterprisePaid, driverTrial, driverPaid)

fun OptimizationLimits.toDto() = OptimizationLimitsDto(enterpriseTrial, enterprisePaid, driverTrial, driverPaid)

fun AdminAccountOptimizationQuotaDto.toDomain() = AdminAccountOptimizationQuota(
    account = account.toDomain(),
    accountKind = accountKind,
    enterpriseName = enterpriseName,
    quota = quota.toDomain(),
)
